import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import _isUndefined from 'lodash/isUndefined';
import { Ref, forwardRef, useEffect, useRef, useState } from 'react';
import { ControllerRenderProps } from 'react-hook-form';
import styled, { css, keyframes } from 'styled-components';

import { SearchableTextInput } from '../Form/SearchableTextInput/SearchableTextInput';
import { toasts } from '../Toast';
import { formatCustomAttributes } from './helper';

// Keyframes for the loader animation
const pulse = keyframes`
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(0.8);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

// Styled components for the editor and loader
const NectedEditorIframeWrapper = styled.iframe<{ isLoading?: boolean }>`
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 4px;
  transition: opacity 0.3s ease;
  opacity: ${({ isLoading }) => (isLoading ? 0.3 : 1)};
  background-color: #f8f9fa;
`;

const LoaderOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.7);
  border-radius: 4px;
  z-index: 10;
  animation: ${fadeIn} 0.3s ease;
  
  .loader-text {
    margin-top: 12px;
    color: #495057;
    font-size: 14px;
    font-weight: 500;
  }
  
  .error-message {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 20px;
    
    span {
      color: #dc3545;
      margin-bottom: 16px;
      font-size: 14px;
    }
    
    button {
      padding: 8px 16px;
      background-color: #4361ee;
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-weight: 500;
      transition: background-color 0.2s;
      
      &:hover {
        background-color: #3a56d4;
      }
    }
  }
`;

const PulseLoader = styled.div`
  display: flex;
  align-items: center;
  
  div {
    width: 12px;
    height: 12px;
    margin: 0 4px;
    border-radius: 50%;
    background-color: var(--color-primary1);
    animation: ${pulse} 1.4s infinite ease-in-out;
    
    &:nth-child(1) {
      animation-delay: 0s;
    }
    
    &:nth-child(2) {
      animation-delay: 0.2s;
    }
    
    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }
`;

const ALLOWED_DOMAINS: string[] = [
  'https://editor.dev.nected.io',
  'https://editor.stage.nected.io',
  'https://editor.nected.io',
  'https://editor.nected.ai',
  'http://localhost:3001',
];

type NectedEditorRef = HTMLIFrameElement & {
  postMessage: (message: any) => void;
};

type PostMessagePayload = {
  type: string;
  data: any;
  mode: string;
};

const NectedDocumentationLink: string = 'https://docs.nected.ai/nected-docs/';

const DocumentationModeMap: Record<string, string> = {
  js: 'code-and-database-queries/custom-javascript-editor',
  list: 'code-and-database-queries/custom-javascript-editor',
  json: 'code-and-database-queries/custom-javascript-editor',
};

export type EditorLanguages =
  | 'js'
  | 'mongo'
  | 'mongodb'
  | 'mysql'
  | 'pgsql'
  | 'bigquery'
  | 'redshift'
  | 'json'
  | 'text'
  | 'sqlserver'
  | 'singleLineText'
  | 'list'
  | 'formula'
  | 'snowflake'
  | 'oracle'
  | '';
export type EditorVariant = 'single' | 'multi';
export type EditorMethods = 'read' | 'write' | 'read,write';

export type NectedSuggestionModel = {
  name?: string;
  value: string;
  meta: string;
  score: number;
  version?: string;
  executedValue?: any;
  display?: boolean;
};

export type RestAPIEditorPayload = {
  name: string;
  value: string;
  type: string;
  prodBaseURL?: string;
  stagBaseURL?: string;
};

export type NectedEditorProps = Partial<Omit<ControllerRenderProps, 'ref'>> & {
  editorDomain : string;
  name?: string;
  value?: string;
  readOnly?: boolean;
  defaultValue?: string | null;
  customSuggestions?: NectedSuggestionModel[];
  minimap?: boolean;
  noSemanticValidation?: boolean;
  noSyntaxValidation?: boolean;
  editorBackground?: string;
  mode?: EditorLanguages;
  variant?: EditorVariant;
  domain?: (typeof ALLOWED_DOMAINS)[number];
  methods?: EditorMethods;
  onSetEditorValidity?: (state: boolean) => void;
  sendReturnType?: boolean;
  setReturnType?: (type: any) => void;
  setExecutedValue?: (execValue: any) => void;
  setHasEditorChanged?: (state: boolean) => void;
  restApiPayload?: RestAPIEditorPayload[];
  handleGetExecData?: (state: any) => void;
  execValues?: Record<string, any>;
  resetValue?: any;
  onReset?: (resetState?: any) => void;
  id?: string;
  editorMetaInfo?: Record<string, any>;
  loaderText?: string;
};

export const NectedEditor = forwardRef(
  (
    {
      name,
      mode = '',
      variant = 'multi',
      onChange,
      value,
      readOnly = false,
      customSuggestions = [],
      domain = 'https://editor.stage.nected.io',
      methods = 'read,write',
      onSetEditorValidity,
      defaultValue,
      sendReturnType = false,
      setReturnType,
      setExecutedValue,
      setHasEditorChanged,
      restApiPayload = [],
      handleGetExecData,
      execValues = {},
      resetValue = null,
      onReset,
      id = 'nected-editor-iframe',
      editorMetaInfo = {},
      loaderText = 'Loading editor...',
      editorDomain=''
    }: NectedEditorProps,
    ref: Ref<NectedEditorRef>
  ) => {
    const editorRef = useRef<HTMLIFrameElement>(null);
    const [cacheBust] = useState(new Date().toISOString());
    const [isIframeLoaded, setIsIframeLoaded] = useState(false);
    const [isEditorReady, setIsEditorReady] = useState(false);
    const [loadingState, setLoadingState] = useState<'initial' | 'loading' | 'loaded' | 'error'>('initial');
    
    useEffect(() => {
      if (mode !== 'singleLineText') {
        window.addEventListener('message', receivedMessage, false);
      }

      return () => {
        window.removeEventListener('message', receivedMessage, false);
      };
    }, []);

    const handleEditorChange = (value?: string, isValid: boolean = true) => {
      if (!_isUndefined(value) && !_isUndefined(onChange)) {
        onChange(value);

        if (typeof onSetEditorValidity === 'function') {
          onSetEditorValidity(isValid);
        }
      }
    };
    const iframeCurrent = editorRef.current;

    useEffect(() => {
      iframeCurrent?.addEventListener('load', () => {});

      return () => {
        iframeCurrent?.removeEventListener('load', () => {});
      };
    }, [iframeCurrent]);

    const sendPostMessage = (message: PostMessagePayload) => {
      setTimeout(() => {
        if (!_isNil(editorRef?.current?.contentWindow)) {
          editorRef.current?.contentWindow.postMessage(message, '*');
        }
      }, 100);
    };

    const receivedMessage = (event: MessageEvent) => {
      const parentDomain = editorDomain;

      const frameDomain = event.origin;

      if (parentDomain === frameDomain || _isEmpty(parentDomain)) {
        if (event.data.type === 'EDITOR_LOADED') {
          // Mark editor as fully loaded and ready
          setIsEditorReady(true);
          setLoadingState('loaded');
        } 
        
        if (event.data.type === 'ONCHANGE') {
          handleEditorChange(event.data.data, event.data.isValid);

          if (typeof setHasEditorChanged === 'function') {
            setHasEditorChanged(true);
          }
        } else if (event.data.type === 'EXECUTION_COMPLETED') {
          if (typeof setReturnType === 'function') {
            setReturnType(event.data.dataType);
          }

          if (typeof setExecutedValue === 'function') {
            if (
              event.data.status === 'ERROR' &&
              typeof event.data.data === 'string' &&
              event.data.data.toLowerCase() !== 'failed to fetch'
            ) {
              toasts.error(event.data.data ?? 'Unable to save', 'toast-error');
            }
            setExecutedValue(
              event.data.status !== 'ERROR' ? event.data.data : undefined
            );
          }
        } else if (event.data.type === 'FIND_EXEC_VALUE') {
          if (typeof handleGetExecData === 'function') {
            handleGetExecData(event.data.data);
          }
        }
      }
    };

    useEffect(() => {
      if ((_isEmpty(value) || _isNil(value)) && !_isEmpty(defaultValue)) {
        sendPostMessage({ type: 'RESET_VALUE', data: defaultValue, mode });
      }
    }, [JSON.stringify(defaultValue)]);

    useEffect(() => {
      if (!_isNil(resetValue)) {
        sendPostMessage({ type: 'RESET_VALUE', data: resetValue, mode });

        if (!_isNil(onReset)) {
          onReset(resetValue);
        }
      }
    }, [JSON.stringify(resetValue)]);

    useEffect(() => {
      if (!_isEmpty(execValues)) {
        sendPostMessage({ type: 'FOUND_EXEC_VALUE', data: execValues, mode });
      }
    }, [JSON.stringify(execValues)]);

    // Set a timeout to show error state if editor takes too long to load
    useEffect(() => {
      if (loadingState === 'loading') {
        const timeoutId = setTimeout(() => {
          if (loadingState === 'loading' && !isEditorReady) {
            setLoadingState('error');
          }
        }, 20000); // 20 seconds timeout
        
        return () => clearTimeout(timeoutId);
      }
    }, [loadingState, isEditorReady]);

    const handleIframeLoad = () => {
      setIsIframeLoaded(false);
      setLoadingState('loading');
      
      setTimeout(() => {
        sendPostMessage({
          type: 'SET_AUTH',
          data: {
            at: `Bearer ${
              window.localStorage.getItem('accessToken') as string
            }`,
            ws:
              window.sessionStorage.getItem('workspaceUUID') ??
              window.localStorage.getItem('workspaceUUID'),
            timezone: window.sessionStorage.getItem('nected-tz'),
            dateFormat: window.sessionStorage.getItem('nected-df'),
          },
          mode,
        });

        sendPostMessage({
          type: 'REST_CONNECTORS',
          data: restApiPayload,
          mode,
        });
        setIsIframeLoaded(true);

        sendPostMessage({
          type: 'SET_VALUE',
          data: !_isEmpty(value) ? value : defaultValue,
          mode,
        });
        sendPostMessage({
          type: 'EDITOR_INFO',
          data: {
            documentation: `${NectedDocumentationLink}${DocumentationModeMap[mode]}`,
            meta: editorMetaInfo,
          },
          mode,
        });
      }, 250);
    };

    useEffect(() => {
      if (customSuggestions.length !== 0 && isIframeLoaded) {
        sendPostMessage({
          type: 'SUGGESTION',
          data: formatCustomAttributes(customSuggestions),
          mode,
        });
      } else if (customSuggestions.length === 0 && isIframeLoaded) {
        sendPostMessage({
          type: 'SUGGESTION',
          data: [],
          mode,
        });
      }
    }, [JSON.stringify(customSuggestions), isIframeLoaded]);

    useEffect(() => {
      if (
        sendReturnType &&
        typeof setReturnType === 'function' &&
        (mode === 'js' ||
          mode === 'json' ||
          mode === 'list' ||
          mode === 'formula')
      ) {
        sendPostMessage({
          type: 'EXECUTE_JS_FORMULA',
          data: {
            suggestions: formatCustomAttributes(customSuggestions),
          },
          mode,
        });
      }
    }, [sendReturnType]);

    useEffect(() => {
      if (restApiPayload.length > 0) {
        sendPostMessage({
          type: 'REST_CONNECTORS',
          data: restApiPayload,
          mode,
        });
      }
    }, [restApiPayload]);

    const getEditorComponent = () => {
      switch (mode) {
        case 'singleLineText':
          return (
            <SearchableTextInput
              name={mode}
              value={value}
              updateValue={(value) => {
                if (!_isUndefined(onChange)) {
                  onChange(value);
                }
              }}
              options={customSuggestions.filter(
                (token) => token.name !== '{{.custom.sql}}'
              )}
              readOnly={readOnly}
            />
          );
        default:
          return (
            <div style={{ position: 'relative', width: '100%', height: '100%' }}>
              <NectedEditorIframeWrapper
                id={id}
                ref={editorRef}
                src={`${domain}?mode=${mode}&variant=${variant}&readOnly=${
                  readOnly as unknown as string
                }&methods=${methods}&cacheBust=${cacheBust}`}
                onLoad={handleIframeLoad}
                isLoading={loadingState === 'loading' || loadingState === 'initial'}
              />
              
              {(loadingState === 'loading' || loadingState === 'initial') && (
                <LoaderOverlay>
                  <PulseLoader>
                    <div></div>
                    <div></div>
                    <div></div>
                  </PulseLoader>
                  <div className="loader-text">{loaderText}</div>
                </LoaderOverlay>
              )}
              
              {loadingState === 'error' && (
                <LoaderOverlay>
                  <div className="error-message">
                    <span>Failed to load the editor. Try refreshing the page.</span>
                    <button onClick={() => {
                      setLoadingState('loading');
                      if (editorRef.current) {
                        const src = editorRef.current.src;
                        editorRef.current.src = '';
                        setTimeout(() => {
                          if (editorRef.current) {
                            editorRef.current.src = src;
                          }
                        }, 100);
                      }
                    }}>
                      Retry
                    </button>
                  </div>
                </LoaderOverlay>
              )}
            </div>
          );
      }
    };

    return <>{getEditorComponent()}</>;
  }
);

NectedEditor.displayName = 'NectedEditor';